import { Button, Icon, Input } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import { withoutEmpty } from "./blockContent";
import { BackgroundField, RichTextField, useUploadTracker } from "./blockEditorParts";
import type {
  BlockContentErrors,
  CardGridCard,
  CardGridCardStyle,
  CardGridColumns,
  CardGridContent,
} from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type CardGridEditorProps = {
  value: CardGridContent;
  onChange: (value: CardGridContent) => void;
  errors?: BlockContentErrors;
  onUploadingChange?: (uploading: boolean) => void;
};

type CardGridFormValues = Omit<CardGridContent, "columns" | "cardStyle"> & {
  columns: "" | `${CardGridColumns}`;
  cardStyle: "" | CardGridCardStyle;
};

const COLUMN_CHOICES: CardGridColumns[] = [2, 3, 4];

const OPTIONAL_CARD_FIELDS = ["subTitle", "badge", "image", "imageAlt"];

function getNextId(cards: CardGridCard[]): number {
  if (cards.length === 0) return 1;
  return Math.max(...cards.map(c => c.id)) + 1;
}

function toContent(initial: CardGridContent, values: CardGridFormValues): CardGridContent {
  const { columns, cardStyle, ...fields } = values;
  const { columns: _columns, cardStyle: _cardStyle, ...rest } = initial;
  return {
    ...rest,
    ...fields,
    cards: fields.cards.map(card => withoutEmpty(card, OPTIONAL_CARD_FIELDS)),
    ...(columns ? { columns: Number(columns) as CardGridColumns } : {}),
    ...(cardStyle ? { cardStyle } : {}),
  };
}

export function CardGridEditor({
  value,
  onChange,
  errors = {},
  onUploadingChange,
}: CardGridEditorProps) {
  const [initial] = useState(value);
  const methods = useForm<CardGridFormValues>({
    defaultValues: {
      ...value,
      columns: value.columns ? `${value.columns}` : "",
      cardStyle: value.cardStyle ?? "",
    },
  });

  const { control, register, watch, getValues, setValue } = methods;

  const { fields, append, remove, move } = useFieldArray<CardGridFormValues>({
    control,
    name: "cards",
  });
  const { uploading, report } = useUploadTracker(onUploadingChange);

  useContentChange(watch, onChange, values => toContent(initial, values));
  const linkCards = watch("cardStyle") !== "text";

  function handleAddCard() {
    const currentCards = getValues("cards");
    const newCard: CardGridCard = {
      id: getNextId(currentCards),
      order: currentCards.length,
      title: "",
      href: "",
      text: "",
    };
    append(newCard);
  }

  function handleMoveCard(index: number, direction: "up" | "down") {
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= fields.length) return;
    move(index, target);
    getValues("cards").forEach((_, position) => {
      setValue(`cards.${position}.order`, position);
    });
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.cardEditorList}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="card-grid-heading">
            Heading (optional)
          </label>
          <Input id="card-grid-heading" {...register("heading")} />
        </div>
        <RichTextField
          control={control}
          name="intro"
          id="card-grid-intro"
          label="Intro (optional)"
        />

        <div className={styles.cardEditorFieldRow}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="card-grid-columns">
              Columns
            </label>
            <select id="card-grid-columns" className={styles.selectField} {...register("columns")}>
              <option value="">Default (2)</option>
              {COLUMN_CHOICES.map(columns => (
                <option key={columns} value={`${columns}`}>
                  {columns}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="card-grid-card-style">
              Card style
            </label>
            <select
              id="card-grid-card-style"
              className={styles.selectField}
              {...register("cardStyle")}
            >
              <option value="">Default (link cards)</option>
              <option value="link">Link cards</option>
              <option value="text">Text cards</option>
            </select>
          </div>
        </div>

        {fields.map((field, index) => (
          <div key={field.id} className={styles.cardEditorItem}>
            <div className={styles.cardEditorItemHeader}>
              <span className={styles.cardEditorItemTitle}>Card {index + 1}</span>
              <div className={styles.cardEditorItemActions}>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => handleMoveCard(index, "up")}
                  disabled={uploading || index === 0}
                  aria-label={`Move card ${index + 1} up`}
                >
                  <Icon icon="chevron-up" size="small" />
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => handleMoveCard(index, "down")}
                  disabled={uploading || index === fields.length - 1}
                  aria-label={`Move card ${index + 1} down`}
                >
                  <Icon icon="chevron-down" size="small" />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => remove(index)}
                  disabled={uploading}
                  aria-label={`Delete card ${index + 1}`}
                >
                  <Icon icon="delete" size="small" />
                </Button>
              </div>
            </div>

            <div className={styles.cardEditorFields}>
              <div className={styles.cardEditorFieldRow}>
                <div className={styles.formField}>
                  <label className={styles.formLabel} htmlFor={`card-grid-order-${field.id}`}>
                    Order
                  </label>
                  <Input
                    id={`card-grid-order-${field.id}`}
                    type="number"
                    {...register(`cards.${index}.order`, { valueAsNumber: true })}
                  />
                </div>
              </div>

              {linkCards && (
                <div className={styles.formField}>
                  <label className={styles.formLabel} htmlFor={`card-grid-sub-title-${field.id}`}>
                    Eyebrow (optional)
                  </label>
                  <Input
                    id={`card-grid-sub-title-${field.id}`}
                    {...register(`cards.${index}.subTitle`)}
                  />
                </div>
              )}

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`card-grid-title-${field.id}`}>
                  Title
                </label>
                <Input id={`card-grid-title-${field.id}`} {...register(`cards.${index}.title`)} />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`card-grid-badge-${field.id}`}>
                  Badge (optional)
                </label>
                <Input
                  id={`card-grid-badge-${field.id}`}
                  placeholder="NEW"
                  {...register(`cards.${index}.badge`)}
                />
              </div>

              {linkCards && (
                <div className={styles.formField}>
                  <label className={styles.formLabel} htmlFor={`card-grid-href-${field.id}`}>
                    Link (href)
                  </label>
                  <Input id={`card-grid-href-${field.id}`} {...register(`cards.${index}.href`)} />
                </div>
              )}

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`card-grid-text-${field.id}`}>
                  Description
                </label>
                <Input id={`card-grid-text-${field.id}`} {...register(`cards.${index}.text`)} />
              </div>

              {linkCards && (
                <>
                  <Controller
                    name={`cards.${index}.image`}
                    control={control}
                    render={({ field: image }) => (
                      <CmsImageUploadField
                        value={image.value ?? ""}
                        onChange={image.onChange}
                        onUploadingChange={pending => report(field.id, pending)}
                        label="Image"
                        noun="image"
                        cardLabel={`Card ${index + 1}`}
                        previewAlt={`Card ${index + 1} image`}
                        guidance="JPG, PNG, GIF or WebP, up to 20 MiB. Shown at the bottom of the card."
                      />
                    )}
                  />
                  <div className={styles.formField}>
                    <label className={styles.formLabel} htmlFor={`card-grid-image-alt-${field.id}`}>
                      Image description (alt text)
                    </label>
                    <Input
                      id={`card-grid-image-alt-${field.id}`}
                      error={errors[`cards.${index}.imageAlt`]}
                      {...register(`cards.${index}.imageAlt`)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        <Button variant="outlined" color="primary" onClick={handleAddCard} disabled={uploading}>
          <Icon icon="plus" size="small" />
          Add Card
        </Button>

        <BackgroundField register={register} name="background" stored={initial.background} />
      </div>
    </FormProvider>
  );
}
